import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { ChevronDown, Search } from 'lucide-react'
import { designTokens } from '@/utils/design-tokens'
import { DepartmentService } from '@/api/generated/services/DepartmentService'
import { DepartmentResponse } from '@/api/generated/models/DepartmentResponse'
import { PaginatedResponse } from '@/api/generated/models/PaginatedResponse'

interface DepartmentSelectProps {
  selectedDepartmentId: string | null
  selectedDepartmentName: string | null
  onSelect: (departmentId: string, departmentName: string) => void
  disabled?: boolean
  error?: string | null
  placeholder?: string
  workspaceId: string
  // 새로운 props: 외부에서 부서 데이터를 직접 전달받을 수 있음
  departments?: DepartmentResponse[]
  isLoading?: boolean
  departmentsError?: string | null
  // Control whether to show "전체" option (for filtering vs form input)
  showAllOption?: boolean
}

// Custom hook for department data loading
const useDepartments = (workspaceId: string) => {
  const [departments, setDepartments] = useState<DepartmentResponse[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchDepartments = useCallback(async () => {
    if (!workspaceId) return
    
    setIsLoading(true)
    setError(null)
    
    try {
      const response: PaginatedResponse = await DepartmentService.appsDepartmentViewsListDepartments({
        workspaceId,
        page: 1,
        perPage: 100 // Load all departments
      })
      
      setDepartments(response.data as DepartmentResponse[])
    } catch (err) {
      console.error('Failed to fetch departments:', err)
      setError('부서 목록을 불러오는데 실패했습니다.')
    } finally {
      setIsLoading(false)
    }
  }, [workspaceId])

  useEffect(() => {
    fetchDepartments()
  }, [fetchDepartments])

  return { departments, isLoading, error, refetch: fetchDepartments }
}

const DepartmentSelect: React.FC<DepartmentSelectProps> = ({
  selectedDepartmentId,
  selectedDepartmentName,
  onSelect,
  disabled = false,
  error = null,
  placeholder = "부서를 선택하세요",
  workspaceId,
  // 외부에서 전달된 부서 데이터
  departments: externalDepartments,
  isLoading: externalIsLoading,
  departmentsError: externalDepartmentsError,
  // Control "전체" option visibility (default: false for form inputs)
  showAllOption = false
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  
  // 외부 데이터가 있으면 사용하고, 없으면 내부 hook 사용 (하위 호환성)
  const internalData = useDepartments(externalDepartments ? '' : workspaceId) // 외부 데이터가 있으면 내부 API 호출 안함
  
  const departments = externalDepartments || internalData.departments
  const isLoading = externalIsLoading !== undefined ? externalIsLoading : internalData.isLoading
  const fetchError = externalDepartmentsError || internalData.error

  // Filter departments based on search term
  const filteredDepartments = useMemo(() => {
    if (!searchTerm.trim()) return departments
    return departments.filter(department =>
      department.name.toLowerCase().includes(searchTerm.toLowerCase().trim())
    )
  }, [departments, searchTerm])

  const handleSelect = (department: DepartmentResponse) => {
    onSelect(department.id, department.name)
    setIsOpen(false)
    setSearchTerm('') // Clear search when selecting
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onSelect('', '')
    setSearchTerm('') // Clear search when clearing selection
  }

  const handleOpen = () => {
    if (!disabled) {
      setIsOpen(!isOpen)
      if (!isOpen) {
        setSearchTerm('') // Clear search when opening dropdown
      }
    }
  }

  return (
    <div className="relative">
      {/* Selection Button */}
      <button
        type="button"
        onClick={handleOpen}
        disabled={disabled}
        className="w-full flex items-center gap-1.5 px-3 py-0 h-10 border-2 rounded cursor-pointer transition-all duration-150 text-left justify-between"
        style={{
          backgroundColor: disabled ? designTokens.colors.gray[0] : designTokens.colors.white,
          borderColor: error ? designTokens.colors.error.DEFAULT : designTokens.colors.gray[1],
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: designTokens.typography.fontSize['body-medium'][0],
          lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
          color: selectedDepartmentName ? designTokens.colors.gray[6] : designTokens.colors.gray[3]
        }}
      >
        <div className="flex-1 flex justify-between items-center">
          <span>{selectedDepartmentName || placeholder}</span>
          <div className="flex items-center gap-2">
            {selectedDepartmentId && selectedDepartmentId.trim() !== '' && (
              <span
                onClick={handleClear}
                className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                style={{ fontSize: '14px' }}
              >
                ✕
              </span>
            )}
            <ChevronDown
              className="w-5 h-5"
              style={{ color: designTokens.colors.gray[3] }}
            />
          </div>
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute z-50 w-full mt-1 bg-white border-2 rounded shadow-lg"
          style={{
            borderColor: designTokens.colors.gray[1]
          }}
        >
          {/* Search Input */}
          <div className="p-3 border-b" style={{ borderBottomColor: designTokens.colors.gray[1] }}>
            <div className="relative">
              <Search
                className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
                style={{ color: designTokens.colors.gray[3] }}
              />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="부서명 검색..."
                className="w-full pl-2 pr-3 py-2 border rounded"
                style={{
                  borderColor: designTokens.colors.gray[1],
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '14px',
                  color: designTokens.colors.gray[6]
                }}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
          </div>

          {/* Dropdown Content */}
          <div className="max-h-48 overflow-y-auto">
            {isLoading ? (
            <div 
              className="p-3 text-center"
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '16px',
                color: designTokens.colors.gray[4]
              }}
            >
              부서 목록을 불러오는 중...
            </div>
          ) : fetchError ? (
            <div 
              className="p-3 text-center"
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '16px',
                color: designTokens.colors.error.DEFAULT
              }}
            >
              {fetchError}
            </div>
          ) : departments.length === 0 ? (
            <div
              className="p-3 text-center"
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '16px',
                color: designTokens.colors.gray[4]
              }}
            >
              부서가 없습니다.
            </div>
          ) : filteredDepartments.length === 0 ? (
            <div
              className="p-3 text-center"
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '16px',
                color: designTokens.colors.gray[4]
              }}
            >
              검색 결과가 없습니다.
            </div>
          ) : (
            <>
              {/* All Departments Option - only show if showAllOption is true and no search term */}
              {showAllOption && !searchTerm.trim() && (
                <button
                  type="button"
                  onClick={() => {
                    onSelect('', '')
                    setIsOpen(false)
                    setSearchTerm('')
                  }}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors"
                  style={{
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: '16px',
                    color: designTokens.colors.gray[6]
                  }}
                >
                  전체
                </button>
              )}

              {filteredDepartments.map((department) => (
                <button
                  key={department.id}
                  type="button"
                  onClick={() => handleSelect(department)}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors"
                  style={{
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: '16px',
                    color: designTokens.colors.gray[6]
                  }}
                >
                  {department.name}
                </button>
              ))}
            </>
          )}
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div 
          className="mt-1 text-sm"
          style={{ 
            color: designTokens.colors.error.DEFAULT,
            fontFamily: designTokens.typography.fontFamily.sans[0]
          }}
        >
          {error}
        </div>
      )}

      {/* Click Outside Handler */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  )
}

export default DepartmentSelect